import type { EventTypes } from '@/types/fcc'
import { useFCCFormatting } from '@/util/fcc/useFCCFormatting'

/**
 * Fallback for the Cases page Alert Type filter. The live catalogue comes from
 * `GET /fcc/event-types`; that fetch never retries and shows no toast, so this
 * list stands in rather than leaving the filter empty and unusable.
 *
 * API values (`Alert.EVENT_TYPE`), EXCPECTED misspellings and all — labels come
 * from formatEventType.
 */
export const ALERT_EVENT_TYPES = [
  'HIGH_AUTHORIZATION_RATE',
  'HIGH_CHARGEBACK_COUNT',
  'HIGH_EXCPECTED_DAILY_VOLUME',
  'HIGH_REFUND_RATE',
  'HIGH_TRANSACTION_COUNT_OUTLIER',
  'HIGH_VOLUME_OUTLIER',
  'HIGH_VOLUME_PER_TRANSACTION',
  'LARGE_SINGLE_TRANSACTION',
  'LOW_AUTHORIZATION_RATE',
  'LOW_BIN_MATCH_RATE',
  'LOW_EXCPECTED_DAILY_VOLUME',
  'LOW_TRANSACTION_COUNT',
  'LOW_TRANSACTION_COUNT_OUTLIER',
  'LOW_VOLUME_OUTLIER',
  'LOW_VOLUME_TREND',
  'MERCHANT_SILENCE',
  'NEGATIVE_SETTLEMENT_ANOMALIES',
  'NEW_MERCHANT_VOLUME',
] as const

export interface EventTypeOption {
  value: string
  label: string
}

/**
 * Alert Type options, alphabetical by label, from the store's catalogue or the
 * fallback when it is empty. Every type is offered whether or not a case carries
 * it — selecting an unused one empties the queue.
 *
 * The blank EVENT_TYPE_ID 0 row is dropped here too, not just upstream.
 */
export const buildEventTypeOptions = (
  eventTypes: EventTypes = [],
): EventTypeOption[] => {
  const { formatEventType } = useFCCFormatting()

  const fromApi = eventTypes
    .filter(type => type.EVENT_TYPE_ID !== 0 && !!type.EVENT_TYPE)
    .map(type => type.EVENT_TYPE as string)

  const eventTypeValues = fromApi.length > 0 ? fromApi : [...ALERT_EVENT_TYPES]

  return Array.from(new Set(eventTypeValues))
    .map(eventType => ({
      value: eventType,
      label: formatEventType(eventType),
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    )
}
